import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  resolveInvoicePayUiState,
  type PublicInvoicePay,
  type InvoicePayUiState,
} from '@entities/invoice-pay';
import { buildMockInvoice, type InvoicePayMockScenario } from '@entities/invoice-pay';
import type { UseInvoicePayResult } from './use-invoice-pay';

const CHECK_COOLDOWN_MS = 15_000;

export interface UseInvoicePayMockResult extends UseInvoicePayResult {
  mockScenario: InvoicePayMockScenario;
  setMockScenario: (scenario: InvoicePayMockScenario) => void;
  isMock: true;
}

export function useInvoicePayMock(invoiceId: string | undefined): UseInvoicePayMockResult {
  const [mockScenario, setMockScenarioState] = useState<InvoicePayMockScenario>('details');
  const [invoice, setInvoice] = useState<PublicInvoicePay | null>(() =>
    buildMockInvoice(invoiceId ?? 'mock', 'details')
  );
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [isProceeding, setIsProceeding] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [checkCooldownLeft, setCheckCooldownLeft] = useState(0);
  const [selectedCurrency, setSelectedCurrency] = useState('USDT');
  const [selectedNetwork, setSelectedNetwork] = useState('TRC20');
  const [checkingAfterExpiry, setCheckingAfterExpiry] = useState(false);
  const [announceKey, setAnnounceKey] = useState<'paymentDetected' | 'paymentConfirmed' | null>(
    null
  );

  const lastCheckAt = useRef(0);

  const setMockScenario = useCallback(
    (scenario: InvoicePayMockScenario) => {
      setMockScenarioState(scenario);
      setAnnounceKey(null);
      setCheckingAfterExpiry(false);

      if (scenario === 'loading') {
        setIsLoading(true);
        setLoadError(false);
        setInvoice(null);
        return;
      }

      if (scenario === 'load_error') {
        setIsLoading(false);
        setLoadError(true);
        setInvoice(null);
        return;
      }

      setIsLoading(false);
      setLoadError(false);
      const next = buildMockInvoice(invoiceId ?? 'mock', scenario);
      setInvoice(next);
      if (next) {
        setSelectedCurrency(next.currency);
        setSelectedNetwork(next.network);
      }
      if (scenario === 'payment_detected') setAnnounceKey('paymentDetected');
      if (scenario === 'success' || scenario === 'success_overpaid') {
        setAnnounceKey('paymentConfirmed');
      }
    },
    [invoiceId]
  );

  const uiState: InvoicePayUiState = useMemo(() => {
    if (mockScenario === 'loading') return 'LOADING';
    if (loadError && !invoice) return 'ERROR';
    if (isLoading && !invoice) return 'LOADING';
    return resolveInvoicePayUiState(invoice, { checkingAfterExpiry });
  }, [checkingAfterExpiry, invoice, isLoading, loadError, mockScenario]);

  useEffect(() => {
    if (checkCooldownLeft <= 0) return undefined;
    const id = window.setInterval(() => {
      setCheckCooldownLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [checkCooldownLeft]);

  const reload = useCallback(async () => {
    setMockScenario(mockScenario === 'load_error' ? 'details' : mockScenario);
  }, [mockScenario, setMockScenario]);

  const proceedToPayment = useCallback(async (): Promise<boolean> => {
    setIsProceeding(true);
    await new Promise((r) => window.setTimeout(r, 400));
    setMockScenario('payment');
    setIsProceeding(false);
    return true;
  }, [setMockScenario]);

  const forceCheck = useCallback(async (): Promise<'found' | 'not_found' | 'error' | 'rate_limited'> => {
    const now = Date.now();
    if (lastCheckAt.current > 0 && now - lastCheckAt.current < CHECK_COOLDOWN_MS) {
      return 'rate_limited';
    }
    setIsChecking(true);
    lastCheckAt.current = now;
    setCheckCooldownLeft(Math.ceil(CHECK_COOLDOWN_MS / 1000));
    await new Promise((r) => window.setTimeout(r, 500));
    setIsChecking(false);

    if (mockScenario === 'payment_detected' || mockScenario === 'confirming') {
      return 'found';
    }
    if (mockScenario === 'success' || mockScenario === 'success_overpaid') {
      return 'found';
    }
    return 'not_found';
  }, [mockScenario]);

  const onTimerExpire = useCallback(async () => {
    setMockScenario('expired');
  }, [setMockScenario]);

  return {
    invoice,
    uiState,
    isLoading,
    loadError,
    isProceeding,
    isChecking,
    checkCooldownLeft,
    selectedCurrency,
    selectedNetwork,
    setSelectedCurrency,
    setSelectedNetwork,
    reload,
    proceedToPayment,
    forceCheck,
    onTimerExpire,
    pollErrorStreak: 0,
    announceKey,
    mockScenario,
    setMockScenario,
    isMock: true,
  };
}
